import React, { memo } from 'react';
import { Release } from '../types';
import { useReleases } from '../context/ReleasesContext';

interface ExploreReleaseCardProps {
  release: Release;
  labelId: number;
  labelName: string;
  isChecked: boolean;
  onToggleCheck: (releaseId: number) => void;
  onTagClick?: (tag: string) => void;
  selectedGenres: string[];
}

const ExploreReleaseCard: React.FC<ExploreReleaseCardProps> = memo(({ 
  release, 
  labelId, 
  labelName,
  isChecked,
  onToggleCheck,
  onTagClick,
  selectedGenres
}) => {
  const { loadingReleaseId } = useReleases();
  const isLoading = !!(release.id && loadingReleaseId === release.id);

  return (
    <div className="rounded-lg overflow-hidden w-full hover:shadow-lg transition-shadow flex h-[320px] mb-4">
      {/* Left side: Image and info in a vertical layout */}
      <a 
        href={release.release_url} 
        target="_blank" 
        rel="noopener noreferrer" 
        className="w-48 min-w-[12rem] flex-shrink-0 flex flex-col group cursor-pointer rounded-lg overflow-hidden"
      >
        {/* Image container */}
        <div className="relative">
          <img
            src={release.image_url!}
            alt={`${release.title} by ${release.artist}`}
            className="w-48 h-48 object-cover"
            loading="lazy"
          />
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleCheck(release.id!);
            }}
            disabled={isLoading}
            className={`absolute bottom-2 right-2 p-2 rounded-full ${
              isChecked ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-800/80 hover:bg-gray-700/80'
            } text-white transition-colors ${
              isLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isLoading ? (
              <svg className="h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : isChecked ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
        </div>
        
        {/* Info section */}
        <div className="p-4 pb-6 bg-gray-50 flex-shrink-0 flex-grow flex flex-col">
          <h3 className="font-semibold text-gray-800 truncate mb-1" title={release.title}>
            {release.title}
          </h3>
          <p className="text-sm text-gray-600 truncate mb-1" title={release.artist}>
            {release.artist}
          </p>
          <p className="text-xs text-gray-500 truncate mb-1" title={labelName}>
            {labelName}
          </p>
          <p className="text-xs text-gray-500 truncate mt-auto" title={release.release_date}>
            {release.release_date}
          </p>
        </div>
      </a>

      {/* Right side: Scrollable genre tags with no background */}
      <div className="flex-grow overflow-hidden min-w-0">
        <div className="h-full overflow-y-auto custom-scrollbar p-3">
          <div className="flex flex-wrap gap-2 content-start">
            {release.genres?.map((genre, index) => {
              const isSelected = selectedGenres.includes(genre);
              return (
                <button
                  key={`${genre}-${index}`}
                  onClick={onTagClick ? () => onTagClick(genre) : undefined}
                  className={`py-1 px-2 text-xs rounded-md transition-colors duration-150 whitespace-nowrap inline-block
                    ${isSelected 
                      ? 'bg-violet-500/90 text-white font-medium border border-violet-400' 
                      : 'bg-violet-100/80 text-gray-700 border border-transparent hover:bg-violet-200/90'
                    } cursor-pointer`}
                >
                  {genre}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
});

ExploreReleaseCard.displayName = 'ExploreReleaseCard';

export default ExploreReleaseCard; 